<template>
  <v-container
    v-show="loaded"
    class="pt-16"
  >
    <v-card
      class="mx-auto"
      max-width="800"
      flat
      tile
    >
      <v-card-title>
        <h1
          class="fs-28 text-wrap"
          tabindex="0"
        >
          {{ $bct('Add a child') }}
        </h1>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col>
            <MarkdownContent
              :content="store.state.config.parent_pre_eligibility_instruction_text"
              class="mb-0 fs-16"
            />
          </v-col>
        </v-row>

        <template v-if="!createdChild">
          <v-row>
            <LabeledTextfield
              v-model="firstName"
              @input="changed = true"
              cols="12"
              md="4"
              message="First name"
              mandatory
            />

            <LabeledTextfield
              v-model="middleName"
              @input="changed = true"
              cols="12"
              md="4"
              message="Middle name"
            />

            <LabeledTextfield
              v-model="lastName"
              @input="changed = true"
              cols="12"
              md="4"
              message="Last name"
              mandatory
            />
          </v-row>

          <v-row>
            <LabeledDateOfBirth
              v-model="dob"
              cols="12"
              message="Date of birth"
              mandatory
            />
          </v-row>

          <v-row>
            <v-col class="d-flex justify-end">
              <v-btn
                @click="createChild"
                :disabled="processing || requiredFieldsMissing"
                append-icon="east"
                class="focus-very-visible"
                color="primary"
                data-testid="child-new-next-button"
                size="x-large"
                tabindex="0"
              >
                {{ $bct('Next') }}
              </v-btn>
            </v-col>
          </v-row>
        </template>

        <template v-else>
          <v-card border>
            <v-card-text>
              <v-row
                class="font-weight-bold text-subtitle-1"
                dense
              >
                <v-col>
                  <span>{{ createdChild.name }}</span>
                </v-col>
              </v-row>

              <v-row
                class="font-weight-bold text-subtitle-1"
                dense
              >
                <v-col>
                  <LongDate :date="createdChild.dob" />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </template>
      </v-card-text>
    </v-card>
    <v-dialog
      v-model="existingChildDialogIsVisible"
      max-width="600"
    >
      <v-card
        class="mx-auto"
        max-width="600px"
      >
        <v-card-title>
          <v-row>
            <v-col
              class="d-flex flex-column"
              cols="auto"
            >
              <span class="fs-24">
                {{ $bct('This child already exists') }}
              </span>
              <span class="fs-16">
                {{ $bct('Would you like to continue creating an application?') }}
              </span>
            </v-col>
            <v-spacer />
            <v-col>
              <v-btn
                @click="existingChildDialogIsVisible = false"
                :aria-label="$bct('Close')"
                icon="close"
                variant="text"
              />
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <v-card class="border-sm border-light-gray">
            <v-card-text>
              <v-row>
                <v-col class="d-flex flex-column">
                  <span>{{ fullName(existingChild) }}</span>
                  <span class="d-flex align-items-center c-dark-gray">
                    <v-icon
                      class="fs-14 mr-2 pt-2"
                      icon="cake"
                      size="x-small"
                    />
                    <span class="fs-12">
                      {{ existingChild.dob }}
                    </span>
                  </span>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-btn
            @click="existingChildDialogIsVisible = false"
            variant="text"
          >
            {{ $bct('Cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="existingChild"
            @click="router.push({ name: 'ChildApply', params: { childId: existingChild.id } })"
            color="primary"
          >
            {{ $bct('Yes, create application for existing child') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { capitalize } from '@/plugins/filters';
import LabeledDateOfBirth from '@/shared/components/form/LabeledDateOfBirth.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import MarkdownContent from '@/shared/components/MarkdownContent.vue';
import useApi from '@/shared/composables/useApi';
import useEventBus from '@/shared/composables/useEventBus';

const { api } = useApi();
const eventBus = useEventBus();
const router = useRouter();
const store = useStore();
const $bct = inject('translator');

const existingChildDialogIsVisible = ref(false);
const firstName = ref(null);
const middleName = ref(null);
const lastName = ref(null);
const loaded = ref(false);
const dob = ref(null);
const processing = ref(false);
const createdChild = ref(null);
const existingChild = ref(null);

const requiredFields = computed(() => [firstName, lastName, dob]);
const requiredFieldsMissing = computed(() => requiredFields.value.some((field) => !field.value));

onMounted(() => {
  const unregisteredRoute = {
    name: 'Signup',
    query: {
      action: 'childNew',
    },
  };

  store.dispatch('identify', {
    success: async () => {
      if (store.state.is_anonymous) await router.replace(unregisteredRoute);
      else loaded.value = true;
    },
    failure: async () => {
      await router.replace(unregisteredRoute);
    },
  });
});

async function createChild() {
  const childParams = {
    first_name: firstName.value,
    middle_name: middleName.value,
    last_name: lastName.value,
    dob: dob.value,
  };

  if (requiredFieldsMissing.value) {
    eventBus.error('Please fill out all fields');
    return;
  }
  processing.value = true;

  const childrenResp = await api.parent.children.index(store.state.profile.group_id);
  const children = childrenResp.data;

  if (children.some((child) => childAlreadyExists(child))) {
    existingChildDialogIsVisible.value = true;
    processing.value = false;
    return;
  }

  const res = await api.parent.children.create(store.state.profile.group_id, childParams);
  if (res.status !== 201) return;
  createdChild.value = res.data;

  await router.push({ name: 'ChildApply', params: { childId: createdChild.value.id } });
  processing.value = false;
}

function childAlreadyExists(child) {
  if (
    cleanName(child.first_name)?.toLowerCase() === cleanName(firstName.value)?.toLowerCase() &&
    cleanName(child.last_name)?.toLowerCase() === cleanName(lastName.value)?.toLowerCase() &&
    child.dob === dob.value
  ) {
    existingChild.value = child;
    return true;
  }

  return false;
}

function cleanName(name) {
  return name?.trim().replace(/\s+/g, ' ');
}

function fullName(child) {
  let result = capitalize(child.first_name);
  if (child.middle_name) result += ` ${capitalize(child.middle_name)}`;
  result += ` ${capitalize(child.last_name)}`;

  return result;
}
</script>

<style>
.wider-btn {
  min-width: 150px !important;
}
</style>
